import React from "react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Analytics({ members, polls }) {
  const now = new Date();

  const last6Months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { year: d.getFullYear(), month: d.getMonth(), label: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` };
  });

  const growthData = last6Months.map(({ year, month, label }) => {
    const count = members.filter(m => {
      if (!m.createdAt) return false;
      const d = new Date(m.createdAt);
      return d.getFullYear() === year && d.getMonth() === month;
    }).length;
    return { label, count };
  });
  const maxGrowth = Math.max(...growthData.map(g => g.count), 1);

  const cityMap = {};
  members.forEach(m => {
    const city = (m.city || "Unknown").trim();
    if (!city) return;
    cityMap[city] = (cityMap[city] || 0) + 1;
  });
  const cityData = Object.entries(cityMap).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxCity = Math.max(...cityData.map(c => c[1]), 1);

  const pollData = [...polls]
    .map(p => ({ question: p.question, votes: p.totalVotes || 0, isClosed: Date.now() > p.expiresAt }))
    .sort((a, b) => b.votes - a.votes)
    .slice(0, 6);
  const maxVotes = Math.max(...pollData.map(p => p.votes), 1);

  const totalMembers = members.length;
  const totalVotes = polls.reduce((s, p) => s + (p.totalVotes || 0), 0);
  const citiesCovered = Object.keys(cityMap).length;
  const thisMonthCount = growthData[growthData.length - 1]?.count || 0;

  return (
    <div>
      <div style={S.statsGrid}>
        {[
          { label: "Total Members", value: totalMembers },
          { label: "New This Month", value: thisMonthCount },
          { label: "Cities Covered", value: citiesCovered },
          { label: "Total Poll Votes", value: totalVotes },
        ].map(s => (
          <div key={s.label} style={S.statCard}>
            <p style={S.statLabel}>{s.label}</p>
            <p style={S.statValue}>{s.value}</p>
          </div>
        ))}
      </div>

      <div style={S.section}>
        <p style={S.sectionTitle}>Member Growth (Last 6 Months)</p>
        <div style={S.card}>
          {growthData.every(g => g.count === 0) ? (
            <p style={S.emptyText}>Abhi tak koi data nahi hai.</p>
          ) : (
            <div style={S.chartRow}>
              {growthData.map(g => (
                <div key={g.label} style={S.barCol}>
                  <p style={S.barValue}>{g.count}</p>
                  <div style={S.barTrack}>
                    <div style={{ ...S.barFill, height: `${(g.count / maxGrowth) * 100}%` }} />
                  </div>
                  <p style={S.barLabel}>{g.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={S.section}>
        <p style={S.sectionTitle}>City-wise Members</p>
        <div style={S.card}>
          {cityData.length === 0 ? (
            <p style={S.emptyText}>Abhi tak koi data nahi hai.</p>
          ) : (
            cityData.map(([city, count], i) => (
              <div key={city} style={{ ...S.hBarRow, borderBottom: i < cityData.length - 1 ? "1px solid #ECECEE" : "none" }}>
                <p style={S.hBarLabel}>{city}</p>
                <div style={S.hBarTrackWrap}>
                  <div style={S.hBarTrack}>
                    <div style={{ ...S.hBarFill, width: `${(count / maxCity) * 100}%` }} />
                  </div>
                  <p style={S.hBarValue}>{count}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={S.section}>
        <p style={S.sectionTitle}>Top Polls by Engagement</p>
        <div style={S.card}>
          {pollData.length === 0 ? (
            <p style={S.emptyText}>Abhi tak koi poll nahi hai.</p>
          ) : (
            pollData.map((p, i) => (
              <div key={i} style={{ ...S.hBarRow, borderBottom: i < pollData.length - 1 ? "1px solid #ECECEE" : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={S.hBarLabel}>{p.question}</p>
                  <span style={{ ...S.pollBadge, color: p.isClosed ? "#16A34A" : "#D97706" }}>
                    {p.isClosed ? "Closed" : "Live"}
                  </span>
                </div>
                <div style={S.hBarTrackWrap}>
                  <div style={S.hBarTrack}>
                    <div style={{ ...S.hBarFill, width: `${(p.votes / maxVotes) * 100}%` }} />
                  </div>
                  <p style={S.hBarValue}>{p.votes}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
  statCard: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 14, padding: "14px 12px" },
  statLabel: { margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.3 },
  statValue: { margin: 0, fontSize: 20, fontWeight: 700, color: "#0D1B3E" },
  section: { marginBottom: 16 },
  sectionTitle: { margin: "0 0 10px 4px", fontSize: 12, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 },
  card: { background: "#fff", borderRadius: 14, padding: 16, border: "1px solid #ECECEE" },
  emptyText: { textAlign: "center", color: "#9CA3AF", fontSize: 12, margin: 0, padding: "12px 0" },
  chartRow: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8, height: 160 },
  barCol: { display: "flex", flexDirection: "column", alignItems: "center", flex: 1, height: "100%", justifyContent: "flex-end" },
  barValue: { margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#0D1B3E" },
  barTrack: { width: "60%", flex: 1, display: "flex", alignItems: "flex-end", background: "#F7F7F9", borderRadius: 6, overflow: "hidden" },
  barFill: { width: "100%", background: "linear-gradient(180deg, #1A2F5A, #0D1B3E)", borderRadius: "6px 6px 0 0", minHeight: 3 },
  barLabel: { margin: "6px 0 0", fontSize: 10, color: "#9CA3AF", fontWeight: 600 },
  hBarRow: { padding: "12px 0", display: "flex", alignItems: "center", gap: 12 },
  hBarLabel: { margin: 0, fontSize: 12, fontWeight: 600, color: "#0D1B3E", width: 110, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  hBarTrackWrap: { display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 },
  hBarTrack: { flex: 1, height: 10, background: "#F7F7F9", borderRadius: 6, overflow: "hidden" },
  hBarFill: { height: "100%", background: "linear-gradient(90deg, #1A2F5A, #0D1B3E)", borderRadius: 6, minWidth: 4 },
  hBarValue: { margin: 0, fontSize: 12, fontWeight: 700, color: "#0D1B3E", width: 30, textAlign: "right", flexShrink: 0 },
  pollBadge: { fontSize: 10, fontWeight: 700 },
};